/**
 * EFFET : « Imite ce smiley » en solo. Une série de 3 smileys, 10 s chacun, visage au repos
 * avant chaque smiley ; score moyen sur 100, record gardé dans ce navigateur. Même notation
 * et même jauge que le mode à deux.
 */
import type { CameraFaceSource } from '../detection/faceTracker';
import { openCamera } from '../detection/faceTracker';
import { createLocalFaceSource } from '../detection/localFaceSource';
import type { Blendshapes, FaceFrame } from '../detection/types';
import { TIMINGS } from '../game/stateMachine';
import { baselineOf, createImitationTracker, type ImitationTracker } from '../smiley/score';
import { SMILEYS, SMILEYS_PER_MATCH } from '../smiley/smileys';
import {
  averageScore,
  createSmileySoloState,
  currentSmiley,
  smileySoloStep,
  type SmileySoloEvent,
  type SmileySoloState,
} from '../smiley/soloSession';
import { createRecordBook } from '../solo/records';
import { h, mount } from './dom';

const REST_S = 3;
const IMITATE_MS = TIMINGS.imitateMs;
/** Score de la manche affiché avant le smiley suivant. */
const BETWEEN_MS = 3000;
/** Au plus une photo de la meilleure grimace tous les… */
const PHOTO_MS = 250;
const PHOTO_WIDTH = 320;

function safeStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

/** Photo de la caméra (JPEG en base64), pour montrer sa meilleure grimace. */
function snapshot(video: HTMLVideoElement): string | null {
  if (!video.videoWidth) return null;
  const canvas = document.createElement('canvas');
  canvas.width = PHOTO_WIDTH;
  canvas.height = Math.round((PHOTO_WIDTH * video.videoHeight) / video.videoWidth);
  canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.7);
}

const newSeed = (): number => Math.floor(Math.random() * 2 ** 31);

export function renderSmileySolo(root: HTMLElement, onMenu: () => void): () => void {
  const records = createRecordBook(safeStorage());
  let cleanupGame: (() => void) | null = null;

  const intro = (): void => {
    const b = records.best('smiley');
    const start = h(
      'button',
      { type: 'button', class: 'button', testid: 'smiley-solo-start' },
      'Commencer',
    );
    const back = h('button', { type: 'button', class: 'button secondary' }, '← Menu');
    back.addEventListener('click', onMenu);
    mount(
      root,
      h('h1', { class: 'title' }, 'Imite ce smiley'),
      h(
        'p',
        { class: 'intertitle' },
        `${String(SMILEYS_PER_MATCH)} smileys, 10 s pour imiter chacun. Ton score : la `,
        h('strong', {}, 'ressemblance moyenne'),
        ' sur 100.',
      ),
      h(
        'p',
        { class: 'muted', testid: 'smiley-solo-best' },
        `Ton record : ${b === null ? '—' : `${String(b)} / 100`}`,
      ),
      h('div', { class: 'actions' }, back, start),
    );
    start.addEventListener('click', () => {
      void play().catch((e: unknown) => {
        mount(
          root,
          h(
            'p',
            { class: 'status error', role: 'status', testid: 'status' },
            e instanceof Error ? e.message : String(e),
          ),
        );
      });
    });
  };

  const play = async (): Promise<void> => {
    const stream = await openCamera();
    const video = h('video', {
      autoplay: '',
      muted: '',
      playsinline: '',
      class: 'mirror',
      testid: 'smiley-camera',
    });
    video.muted = true;
    video.srcObject = stream;
    const overlay = h('div', {
      class: 'mirror-overlay',
      testid: 'smiley-solo-overlay',
      role: 'status',
    });
    const roundLine = h('p', { class: 'muted', testid: 'smiley-solo-round' });
    const emoji = h('p', { class: 'smiley-emoji', testid: 'smiley' });
    const label = h('p', { class: 'smiley-label' });
    const liveText = h('span', {});
    const liveBar = h('span', { class: 'pinch-bar' });
    const live = h(
      'p',
      { class: 'pinch-status', testid: 'imitation-live', role: 'status' },
      liveText,
      liveBar,
    );
    const bestEl = h('p', { class: 'muted', testid: 'imitation-best' });
    const timeLeft = h('p', { class: 'muted', testid: 'imitation-time' });
    const result = h('div', { class: 'card result', testid: 'smiley-solo-result', hidden: '' });
    const stopButton = h(
      'button',
      { type: 'button', class: 'button secondary', testid: 'smiley-solo-stop' },
      'Arrêter',
    );
    mount(
      root,
      roundLine,
      emoji,
      label,
      h('div', { class: 'mirror-wrap' }, video, overlay),
      live,
      bestEl,
      timeLeft,
      stopButton,
      result,
    );

    const source = createLocalFaceSource(video);
    let state: SmileySoloState = smileySoloStep(createSmileySoloState(), {
      type: 'start',
      seed: newSeed(),
    });
    let baselineFrames: (Blendshapes | null)[] = [];
    let tracker: ImitationTracker | null = null;
    const photos: (string | null)[] = [];
    let lastPhotoAt = -Infinity;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const later = (ms: number, fn: () => void): void => {
      const id = setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };

    const showLive = (liveScore: number, best: number, now: number): void => {
      live.dataset['score'] = String(liveScore);
      liveText.textContent = `Ressemblance : ${String(liveScore)} %`;
      liveBar.style.setProperty('--progress', String(liveScore / 100));
      bestEl.dataset['best'] = String(best);
      bestEl.textContent = `Ton meilleur (tenu) : ${String(best)} %`;
      const left = Math.max(0, Math.ceil((IMITATE_MS - (now - state.imitateStartedAt)) / 1000));
      timeLeft.textContent = `Encore ${String(left)} s`;
    };

    const dispatch = (event: SmileySoloEvent): void => {
      const previous = state;
      state = smileySoloStep(state, event);
      if (state === previous) return;
      onPhase(previous);
    };

    const startRest = (): void => {
      baselineFrames = [];
      let n = REST_S;
      overlay.className = 'mirror-overlay';
      overlay.textContent = `Visage neutre… le smiley arrive dans ${String(n)}`;
      const tick = (): void => {
        n -= 1;
        if (n > 0) {
          overlay.textContent = `Visage neutre… le smiley arrive dans ${String(n)}`;
          later(1000, tick);
          return;
        }
        dispatch({ type: 'restDone', t: performance.now() });
      };
      later(1000, tick);
    };

    const startImitate = (): void => {
      const id = currentSmiley(state);
      tracker = createImitationTracker(id, baselineOf(baselineFrames));
      photos[state.round] = null;
      lastPhotoAt = -Infinity;
      emoji.textContent = SMILEYS[id].emoji;
      emoji.dataset['smiley'] = id;
      label.textContent = SMILEYS[id].label;
      overlay.className = 'mirror-overlay hidden';
      showLive(0, 0, state.imitateStartedAt);
      later(IMITATE_MS, () => {
        dispatch({ type: 'imitateDone', score: tracker?.best() ?? 0 });
      });
    };

    const showBetween = (): void => {
      const score = state.scores[state.round] ?? 0;
      overlay.className = 'mirror-overlay';
      overlay.textContent = `${SMILEYS[currentSmiley(state)].emoji} ${String(score)} % ! Smiley suivant…`;
      later(BETWEEN_MS, () => {
        dispatch({ type: 'next' });
      });
    };

    const showResult = (): void => {
      const score = averageScore(state.scores);
      const { isRecord, best: bestScore } = records.submit('smiley', score);
      const again = h(
        'button',
        { type: 'button', class: 'button', testid: 'smiley-solo-again' },
        'Rejouer',
      );
      const menu = h('button', { type: 'button', class: 'button secondary' }, 'Menu');
      again.addEventListener('click', () => {
        dispatch({ type: 'restart', seed: newSeed() });
      });
      menu.addEventListener('click', () => {
        stop();
        intro();
      });
      const rounds = state.scores.map((s, i) => {
        const smiley = SMILEYS[currentSmiley({ ...state, round: i })];
        const photo = photos[i];
        return h(
          'figure',
          {},
          photo
            ? h('img', { src: photo, alt: `Ta grimace : ${smiley.label}`, class: 'mirrored' })
            : h('p', { class: 'smiley-emoji' }, smiley.emoji),
          h('figcaption', {}, `${smiley.emoji} ${String(s)} %`),
        );
      });
      result.replaceChildren(
        h('h2', {}, 'Série terminée !'),
        h('p', { class: 'big-time', testid: 'smiley-solo-score' }, `${String(score)} / 100`),
        h('div', { class: 'snapshots', testid: 'smiley-solo-rounds' }, ...rounds),
        isRecord
          ? h('p', { class: 'record', testid: 'smiley-solo-record' }, 'Nouveau record !')
          : h('p', { class: 'muted' }, `Ton record : ${String(bestScore ?? 0)} / 100`),
        h('div', { class: 'actions' }, menu, again),
      );
    };

    const onPhase = (previous: SmileySoloState): void => {
      result.hidden = state.phase !== 'over';
      stopButton.hidden = state.phase === 'over';
      const playing = state.phase === 'imitate';
      for (const el of [live, bestEl, timeLeft]) el.hidden = !playing;
      for (const el of [emoji, label]) el.hidden = !(playing || state.phase === 'between');
      roundLine.textContent =
        state.phase === 'over'
          ? ''
          : `Smiley ${String(state.round + 1)} / ${String(SMILEYS_PER_MATCH)}`;
      switch (state.phase) {
        case 'rest':
          startRest();
          break;
        case 'imitate':
          startImitate();
          break;
        case 'between':
          showBetween();
          break;
        case 'over':
          overlay.className = 'mirror-overlay hidden';
          if (previous.phase !== 'over') showResult();
          break;
        default:
          break;
      }
    };

    const onFrame = (frame: FaceFrame): void => {
      if (state.phase === 'rest') {
        baselineFrames.push(frame.blendshapes);
        return;
      }
      if (state.phase !== 'imitate' || !tracker) return;
      const sample = tracker.push(frame.t, frame.blendshapes);
      showLive(sample.live, tracker.best(), performance.now());
      if (sample.improved && frame.t - lastPhotoAt >= PHOTO_MS) {
        const data = snapshot(video);
        if (data) {
          photos[state.round] = data;
          lastPhotoAt = frame.t;
        }
      }
    };

    const off = source.subscribe(onFrame);
    const stop = (): void => {
      off();
      if ('close' in source) (source as CameraFaceSource).close();
      else source.stop();
      for (const id of timers) clearTimeout(id);
      for (const track of stream.getTracks()) track.stop();
      cleanupGame = null;
    };
    cleanupGame = stop;
    stopButton.addEventListener('click', () => {
      stop();
      intro();
    });

    onPhase(createSmileySoloState());
    await source.start();
  };

  intro();
  return () => cleanupGame?.();
}
